import React, { useState } from 'react';
import AllFilterCategory from './AllFilterCategory/AllFilterCategory';
import { ReactComponent as Chevron } from '../../../../../assets/icons/chevron.svg';
import { ReactComponent as AmenitiesIcon } from '../../../../../assets/icons/redesign_amenities_icon.svg';
import data from './allFilter.json';
import styles from './AllFilter.module.scss';
import { useResultListContext } from 'common/contexts/ResultListContext';
import {addGTMEvent} from "../../../../../helpers/analytics";

interface Props {
  fetchData: Function,
}

type CategoryFilter = {
  [key: number]: string;
};

type AllFilters = {
  [category: string]: CategoryFilter;
};

const typedData: AllFilters = data;

const AllFilter: React.FC<Props> = ({
  fetchData,
}) => {
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [checkedCount, setCheckedCount] = React.useState(0);

  const {
    activeFilters,
    setActiveFilters,
    currentDuration,
    currentLocation,
    currentResources,
    setCurrentAmenities,
    setCurrentPage,
    budgetValues,
    sortBy,
  } = useResultListContext();
  
  React.useEffect(() => {
    if (isFilterVisible) {
      const originalStyles = {
        overflow: document.body.style.overflow,
      };
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '15px';

      return () => {
        document.body.style.overflow = originalStyles.overflow;
        document.body.style.paddingRight = '0';
      };
    } else {
      return () => {};
    }
  }, [isFilterVisible]);
  const AMENITY_LABEL_BY_ID: Record<string, string> = Object.fromEntries(
      Object.entries(data).flatMap(([_, group]) =>
          Object.entries(group).map(([id, label]) => [id, label.trim()])
      )
  );

// 2) Read names from a query string like "&amenities=1,8,32"
  function amenityNamesFromParam(param: string): string[] {
    // make it work for "&amenities=..." or "?amenities=..." or "amenities=..."
    const qs = new URLSearchParams(
        param.startsWith('&') || param.startsWith('?') ? param.slice(1) : param
    );
    const raw = qs.get('amenities');
    if (!raw) return [];
    return raw
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(id => AMENITY_LABEL_BY_ID[id] ?? id); // fallback to id if missing
  }
  const applyFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const activeFilterKeys = Object.entries(activeFilters)
      .filter(([, value]) => value === true)
      .map(([key]) => key)
      .join(',');
    const request = `&amenities=${activeFilterKeys}`;
    const names = amenityNamesFromParam(request);
    addGTMEvent('amenities_filter_menu_apply', {  'listings_view_type' : viewParam, amenities_selection: names.join(', ') });
    setCurrentAmenities(request);
    setCurrentPage(1);

    fetchData(
      currentLocation.lat,
      currentLocation.lng,
      currentLocation.rad,
      10,
      0,
      currentResources,
      request,
      currentDuration,
      budgetValues,
      sortBy,
    );
    setIsFilterVisible(false);
  };

  const clearFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setActiveFilters({});
    addGTMEvent('amenities_filter_menu_apply', {  'listings_view_type' : viewParam, amenities_selection: {} });
  };

  const handleFilterChange = (filterId: string) => {
    setActiveFilters((prevActiveFilters:any) => {
      const updatedFilters = { ...prevActiveFilters };
      updatedFilters[filterId] = !updatedFilters[filterId];

      return updatedFilters;
    });
  };

  const categories = Object.keys(data);

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevIsFilterVisible) => !prevIsFilterVisible);
    if(isFilterVisible){
      addGTMEvent('amenities_filter_menu_close', {  'listings_view_type' : viewParam });
    } else {
      addGTMEvent('amenities_filter_menu_open', {  'listings_view_type' : viewParam });
    }
  };
  const viewParam = new URLSearchParams(window.location.search).get('view');


  const handleBackgroundClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsFilterVisible(false);
    addGTMEvent('amenities_filter_menu_close', {  'listings_view_type' : viewParam });
    // clear chosen filters if we close popup
    const searchParams = new URLSearchParams(location.search);
    const amenitiesFilter = searchParams.get('amenities')?.replace(/^,+/g, '').replace(/,+/g, ',').replace(/,$/g, '').replace(/^undefined,|,undefined$|,undefined,/g, '').replace(/NaN|NaN,|,NaN$|,NaN,/g, '').split(',') || [''];

    setActiveFilters((prev:any) => {
      const updatedFilters:any = {} ;

      for (const key in prev) {
        updatedFilters[key] = false;
      }

      for (let i = 0; i < amenitiesFilter.length; i++) {
        updatedFilters[amenitiesFilter[i]] = true;
      }
      return updatedFilters;
    })
  };

  React.useEffect(() => {
    const count = Object.entries(activeFilters).filter(([key, value]) => /^\d+$/.test(key) && value === true).length;
    setCheckedCount(count);
  }, [activeFilters]);

  return (
    <button className={isFilterVisible || checkedCount > 0 ? styles.container_active : styles.container} onClick={toggleFilterVisibility} type="button">
      <AmenitiesIcon />
      Amenities
      <span style={{ display: checkedCount ? 'block' : 'none' }}>
        {`(${checkedCount})`}
      </span>
      <Chevron style={{ transform: isFilterVisible ? 'rotate(180deg)' : 'none' }} />
      {isFilterVisible && (
        <div className={styles.background} onClick={handleBackgroundClick}>
          <div
            className={styles.container__filters}
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ display: isFilterVisible ? 'block' : 'none' }}
          >
            {categories.map((category) => (
              <AllFilterCategory
                key={category}
                category={category}
                filters={typedData[category]}
                onFilterChange={handleFilterChange}
              />
            ))}
          </div>
          <div
            className={styles.container__footer}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <span>{`${checkedCount} selected`}</span>
            <div className={styles.container__footer_buttons}>
              <button onClick={(e) => clearFilters(e)} type="button">Clear all</button>
              <button onClick={(e) => applyFilters(e)} type="button">Apply</button>
            </div>
          </div>
        </div>
      )}
    </button>
  );
};

export default AllFilter;
